/**
 * SSO Authentication Utilities
 *
 * Implements OIDC Authorization Code Flow with PKCE against the Cognito Hosted UI,
 * which federates to Okta as the upstream identity provider.
 */

function getSSOConfig() {
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const idpName = process.env.REACT_APP_COGNITO_EXTERNAL_IDP_NAME;

  if (!domain || !clientId) {
    throw new Error(
      'Missing SSO configuration. Set REACT_APP_COGNITO_DOMAIN and REACT_APP_COGNITO_CLIENT_ID environment variables.',
    );
  }

  // Normalise — accept either bare host or full URL, strip trailing slash
  const baseUrl = (domain.startsWith('http') ? domain : `https://${domain}`).replace(/\/$/, '');

  return { baseUrl, clientId, idpName };
}

function getRedirectUri(): string {
  return `${globalThis.location.origin}/sso-callback`;
}

/**
 * Generate a cryptographically random string for PKCE code verifier.
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  globalThis.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate the PKCE code challenge from a code verifier using SHA-256.
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64 URL-safe encoding (no padding).
 */
function base64UrlEncode(buffer: Uint8Array): string {
  let binary = '';
  buffer.forEach((byte) => {
    binary += String.fromCodePoint(byte);
  });
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

/**
 * Generate a random state parameter for CSRF protection.
 */
function generateState(): string {
  const array = new Uint8Array(16);
  globalThis.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Initiates the Okta OIDC Authorization Code Flow with PKCE.
 * Stores PKCE verifier in sessionStorage, then redirects to Cognito/Okta.
 */
export async function initiateOktaLogin(): Promise<void> {
  const config = getSSOConfig();

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  sessionStorage.setItem('okta_code_verifier', codeVerifier);
  sessionStorage.setItem('okta_state', state);

  const authUrl = new URL(`${config.baseUrl}/oauth2/authorize`);
  authUrl.searchParams.set('client_id', config.clientId);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', 'openid profile email');
  authUrl.searchParams.set('redirect_uri', getRedirectUri());
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  if (config.idpName) {
    // Skip Cognito's IdP picker and go straight to Okta
    authUrl.searchParams.set('identity_provider', config.idpName);
  }

  globalThis.location.href = authUrl.toString();
}

/**
 * Exchanges the authorization code for tokens using the PKCE code verifier.
 */
export async function exchangeCodeForTokens(
  code: string,
): Promise<{ idToken: string; accessToken: string }> {
  const config = getSSOConfig();
  const codeVerifier = sessionStorage.getItem('okta_code_verifier');

  if (!codeVerifier) {
    throw new Error('Missing PKCE code verifier. Please try logging in again.');
  }

  const tokenUrl = `${config.baseUrl}/oauth2/token`;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: getRedirectUri(),
    code_verifier: codeVerifier,
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error_description || 'Failed to exchange authorization code.');
  }

  const data = await response.json();

  // Store SSO tokens so the app recognises the user as authenticated
  sessionStorage.setItem('sso_id_token', data.id_token);
  sessionStorage.setItem('sso_access_token', data.access_token);

  // Clean up PKCE sessionStorage
  sessionStorage.removeItem('okta_code_verifier');
  sessionStorage.removeItem('okta_state');

  return {
    idToken: data.id_token,
    accessToken: data.access_token,
  };
}

/**
 * Decodes the JSON payload of a JWT, returning null if it can't be parsed.
 * Handles the base64url alphabet used by JWT segments.
 */
function decodeTokenPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) {
    return null;
  }
  try {
    const base64 = segment.replaceAll('-', '+').replaceAll('_', '/');
    // atob yields a Latin-1 string; percent-encode each byte and decode as UTF-8.
    const HEX_PAD_LENGTH = 2;
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => {
          const hex = (char.codePointAt(0) ?? 0).toString(16).padStart(HEX_PAD_LENGTH, '0');
          return `%${hex}`;
        })
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Returns true when a valid, unexpired Okta SSO session exists in sessionStorage.
 */
export function isSSOAuthenticated(): boolean {
  const idToken = sessionStorage.getItem('sso_id_token');
  if (!idToken) {
    return false;
  }

  const payload = decodeTokenPayload(idToken);
  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }

  // `exp` is seconds since the epoch; treat a missing/past expiry as not authenticated.
  return payload.exp * 1000 > Date.now();
}

/**
 * Decodes the stored Okta id_token and returns identity info.
 */
export function getSSOIdentity(): { email: string; sub: string; groups: string[] } {
  const idToken = sessionStorage.getItem('sso_id_token');
  const payload = idToken ? decodeTokenPayload(idToken) : null;
  if (!payload) {
    return { email: '', sub: '', groups: [] };
  }
  const rawGroups = payload['cognito:groups'] ?? payload.groups;
  const groups = Array.isArray(rawGroups)
    ? rawGroups.filter((g): g is string => typeof g === 'string')
    : [];
  return {
    email: payload.email ?? '',
    sub: payload.sub ?? '',
    groups,
  };
}

/**
 * Clears the stored SSO tokens (used on sign-out).
 */
export function clearSSOSession(): void {
  sessionStorage.removeItem('sso_id_token');
  sessionStorage.removeItem('sso_access_token');
  sessionStorage.removeItem('okta_code_verifier');
  sessionStorage.removeItem('okta_state');
}

/**
 * Validates the state parameter returned from Okta matches the one we sent.
 */
export function validateState(returnedState: string): boolean {
  const storedState = sessionStorage.getItem('okta_state');
  return storedState === returnedState;
}
